import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { Field, TextInput } from "../forms/Fields";
import { Switch } from "../ui/switch";

const EMPTY = { client_name: "", client_email: "", amount: "", description: "", send_email: true };

export const PaymentsPanel = () => {
  const [data, setData] = useState(null);
  const [f, setF] = useState(EMPTY);
  const load = () => api.get("/admin/payments").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const link = (t) => `${window.location.origin}/pay/${t}`;
  const copy = (t) => navigator.clipboard.writeText(link(t)).then(() => toast.success("Payment link copied"));

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data: d } = await api.post("/admin/payments", f);
      toast.success(f.send_email ? (d.email_sent ? "Payment request emailed to your client" : "Created, but the email could not be sent") : "Payment request created");
      setF(EMPTY); load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (!data) return <p className="font-sans text-slate">Loading...</p>;
  return (
    <div className="grid lg:grid-cols-[minmax(0,26rem)_1fr] gap-10 font-sans" data-testid="admin-payments">
      <form onSubmit={create} className="bg-paper rounded-2xl p-6 space-y-5 self-start" aria-label="New payment request" data-testid="payment-request-form">
        <p className="font-display text-2xl">New payment request</p>
        {!data.configured && <p className="text-sm bg-sand rounded-xl p-3" data-testid="paypal-not-configured">PayPal keys aren't added yet. You can create requests now; clients can pay once the keys are in.</p>}
        {data.configured && data.mode !== "live" && <p className="text-sm bg-sky rounded-xl p-3">PayPal is in test (sandbox) mode. No real money moves.</p>}
        <Field id="p-name" label="Client name" required><TextInput id="p-name" required value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} /></Field>
        <Field id="p-email" label="Client email" required><TextInput id="p-email" type="email" required value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} /></Field>
        <Field id="p-amount" label="Amount (USD)" required><TextInput id="p-amount" type="number" min="1" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field id="p-desc" label="What it's for" required hint="e.g. Staff training, March 12"><TextInput id="p-desc" required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} aria-describedby="p-desc-hint" /></Field>
        <div className="flex items-center gap-3"><Switch id="p-send" checked={f.send_email} onCheckedChange={(v) => setF({ ...f, send_email: v })} data-testid="payment-send-email-switch" /><label htmlFor="p-send">Email the payment link to the client</label></div>
        <button type="submit" className="w-full bg-teal text-paper rounded-full py-3 hover:bg-teal-deep" data-testid="payment-create-button">Create payment request</button>
      </form>
      <div className="min-w-0">
        <p className="font-display text-2xl">Payment requests</p>
        {data.items.length === 0 && <p className="mt-4 text-slate">None yet.</p>}
        <ul className="mt-4 space-y-3">
          {data.items.map((p) => (
            <li key={p.token} className="bg-paper rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`payment-${p.token}`}>
              <div><p className="font-semibold">${p.amount} · {p.client_name}</p><p className="text-sm text-slate">{p.description} · {p.client_email}</p></div>
              <div className="flex items-center gap-3">
                <button onClick={() => copy(p.token)} className="inline-flex items-center gap-1.5 text-sm text-teal underline" data-testid={`payment-copy-${p.token}`}><Copy className="w-3.5 h-3.5" aria-hidden="true" />Copy link</button>
                <span className={`lbl rounded-full px-3 py-1 ${p.status === "paid" ? "bg-teal text-paper" : "bg-sand"}`}>{p.status}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
